import { Fragment, useState } from 'react';
// @ts-expect-error @headlessui/react incompatibility with node16 resolution
import { Dialog, Transition } from '@headlessui/react';
import { Heading } from '~/rs-components/elements';
import IconClose from '~/assets/icons/CloseIcon';
import Logo from "../../assets/icons/Logo"
import LogoDrawer from '~/assets/icons/LogoDrawer';

/**
 * Drawer component that opens on user click.
 * @param heading - string. Shown at the top of the drawer.
 * @param open - boolean state. if true opens the drawer.
 * @param onClose - function should set the open state.
 * @param openFrom - right, left
 * @param children - react children node.
 */
function Drawer({
  heading,
  open,
  onClose,
  openFrom = 'right',
  children,
  backgroundColor = '#FAFAFA',
  hideLogo
}: {
  heading?: string;
  open: boolean;
  onClose: () => void;
  openFrom: 'right' | 'left';
  children: React.ReactNode;
  backgroundColor?: string;
  hideLogo?: boolean
}) {
  const offScreen = {
    right: 'translate-x-full',
    left: '-translate-x-full',
  };

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 left-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0">
          <div className="absolute inset-0 overflow-hidden">
            <div
              className={`fixed inset-y-0 flex max-w-full responsiveMenu rightCartMenu ${openFrom === 'right' ? 'right-0' : ''
                }`}
            >
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-300"
                enterFrom={offScreen[openFrom]}
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-300"
                leaveFrom="translate-x-0"
                leaveTo={offScreen[openFrom]}
              >
                <Dialog.Panel className="overflow-hidden w-screen h-screen max-w-lg text-left align-middle transition-all transform shadow-xl bg-contrast">
                  {!hideLogo ? (
                    <header
                      className={`flex items-center px-6 h-nav sm:px-8 md:px-12 ${heading ? 'justify-between' : 'justify-end'
                        }`}
                    >
                      {heading !== null && (
                        <Dialog.Title className="responsiveMenu__logo">
                          <Heading as="span" size="lead" id="cart-contents">
                            <LogoDrawer />
                          </Heading>
                        </Dialog.Title>
                      )}
                      <button
                        type="button"
                        className="p-4 -m-4 transition text-primary hover:text-primary/50 scale-50"
                        onClick={onClose}
                      >
                        <IconClose aria-label="Close panel" width="30" height="30" />
                      </button>
                    </header>
                  ) :
                    <header className={`flex items-center px-6 sm:px-8 md:px-12 justify-end`}>
                      <button
                        type="button"
                        className="p-4 -m-4 transition text-primary hover:text-primary/50"
                        onClick={onClose}
                      >
                        <IconClose aria-label="Close panel" width="30" height="30" />
                      </button>
                    </header>
                  }
                  {children}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

/* Use for associating arialabelledby with the title*/
Drawer.Title = Dialog.Title;

export { Drawer };

export function useDrawer(openDefault = false) {
  const [isOpen, setIsOpen] = useState(openDefault);

  function openDrawer() {
    setIsOpen(true);
  }

  function closeDrawer() {
    setIsOpen(false);
  }

  return {
    isOpen,
    openDrawer,
    closeDrawer,
  };
}